import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, OnDestroy, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ProductGalleryComponent } from '@b2b/components/product/product-gallery/product-gallery.component';
import { ProductPriceComponent } from '@b2b/components/product/product-price/price.component';
import { ProductDescriptionComponent } from '@b2b/components/product/product-tab/product-description/product-description.component';
import { ProductSpecificationsComponent } from '@b2b/components/product/product-tab/product-specifications/product-specifications.component';
import { ToasterService } from '@shared/components/toaster/toaster.service';
import { BreadcrumbService } from '@app/core/services/breadcrumb.service';
import { CartService } from '@b2b/features/cart/core/cart.service';
import { ProductService } from '@b2b/features/product/core/services/product.service';
import { SkeletonLoadingComponent } from '@shared/components/skeleton-loading/skeleton-loading.component';
import { InputQuantityComponent } from '@shared/components/form-elements/input-quantity/input-quantity.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { CartItem } from '@b2b/features/cart/utilities/cart.model';
import { TOASTER_TYPE } from '@shared/components/toaster/toaster.model';
import { take } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { DeliveryTime } from '@b2b/features/product/core/utilities/delivery.model';
import { ModalService } from '@shared/components/modal-list/modal-list.service';
import { initialStatus } from '@app/core/models/request-status.model';

@Component({
  selector: 'cx-product-details-section',
  templateUrl: './cx-product-details-section.component.html',
  styleUrls: ['./cx-product-details-section.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    ReactiveFormsModule,
    ProductPriceComponent,
    ProductDescriptionComponent,
    ProductSpecificationsComponent,
    ProductGalleryComponent,
    SkeletonLoadingComponent,
    InputQuantityComponent,
  ],
})
export class CxProductDetailsSectionComponent implements OnDestroy, OnInit {
  readonly product = toSignal(this.productService.getProduct(), {
    initialValue: { ...initialStatus, data: null },
  });

  readonly price = toSignal(this.productService.getPrice(), {
    initialValue: { ...initialStatus, data: null },
  });
  readonly stock = toSignal(this.productService.getStock(), {
    initialValue: { ...initialStatus, data: null },
  });

  readonly rattingStatus = signal(false);
  readonly qtyControl = new FormControl(1);
  readonly isAddingToCart = signal(false);

  deliveryTime = signal<DeliveryTime | undefined>(undefined);

  constructor(
    private productService: ProductService,
    private cartService: CartService,
    private toasterService: ToasterService,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private destroyRef: DestroyRef,
    private titleService: Title,
    private modalService: ModalService,
  ) {
    effect(() => {
      this.loadProduct();
    });
  }

  ngOnInit(): void {
    this.modalService.skip();
    // this.loadProduct();
  }

  ngOnDestroy(): void {
    this.productService.clearProduct();
  }

  addToCart(): void {
    const product = this.product()?.data;
    const qty = Number(this.qtyControl.value);

    if (!product) return;

    const stock = this.stock()?.data;

    const item: CartItem = {
      warehouse: stock?.cdID || '',
      productCode: product?.code || '',
      qty,
    };

    this.isAddingToCart.set(true);

    this.cartService
      .addCartItem(item)
      .pipe(take(1))
      .subscribe(response => {
        this.isAddingToCart.set(false);

        if (!response.hasError) {
          this.qtyControl.setValue(1);

          this.toasterService.init({
            id: 'toaster-add-to-cart',
            title: 'Produto Adicionado no Carrinho!',
            type: TOASTER_TYPE.UPDATE_CART_ITEM,
            content: '',
            cartItem: {
              cartCode: this.cartService.getCartID(),
              product: product,
            },
          });
        } else {
          this.toasterService.init({
            id: 'toaster-add-to-cart-fail',
            title: response.message,
            type: TOASTER_TYPE.DANGER,
            content: '',
            cartItem: {
              cartCode: this.cartService.getCartID(),
              product: product,
            },
          });
        }
      });
  }

  loadProduct() {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const productID = params['id'];
      if (productID) {
        this.productService.loadProduct(productID);
        this.productService.loadPrice(productID);
        this.productService.loadStock(productID);
      }
    });

    this.productService
      .takeProduct()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(product => {
        if (product.name) {
          this.titleService.setTitle(product.name);
        }

        this.breadcrumbService.setLinks([
          { linkName: 'Home', routerLink: [''] },
          { linkName: product.name, routerLink: ['produto', product.code || '', product.name || ''] },
        ]);
      });
  }
}
